<template>
  <div
    v-show="showStartWidget"
    class="start-widget-container"
    :class="{ skeleton: loading }"
  >
    <modal-base
      :is-open="showModal"
      @close="showModal = false"
    >
      <p v-html="infoHtml"></p>
    </modal-base>
    <div class="column-one">
      <div
        v-if="infoHtml"
        class="info-button"
      >
        <button
          class="clear-button"
          data-test="start-widget-info-button"
          @click="showModal = !showModal"
        >
          <icon name="info-circle" />
        </button>
      </div>
      <div class="spacer"></div>
      <div
        v-if="attachmentUrl && !loading"
        class="app-icon"
      >
        <img
          :src="attachmentUrl"
        />
      </div>
      <div
        class="title"
        data-test="start-widget-title"
      >
        {{ title }}
      </div>
      <div
        class="description"
        data-test="start-widget-description"
      >
        {{ description }}
      </div>
    </div>
    <div class="column-two">
      <div class="question-box">
        <div
          data-test="start-widget-question-stem"
          class="question-stem"
          :class="{ blurred: rateLimitLocked}"
        >
          {{ questionStem }}
        </div>
        <button
          class="start-button start-button--desktop"
          :class="{ loading, 'no-credits-left': rateLimitLocked }"
          data-test="start-widget-start-button"
          :disabled="loading"
          @click="handleClick"
        >
          <icon
            v-if="rateLimitLocked"
            class="button-icon"
            name="lock"
            variant="white"
          />
          {{ buttonText }}
        </button>
      </div>
      <button
        class="start-button--mobile"
        :class="{ loading, 'no-credits-left': rateLimitLocked }"
        data-test="start-widget-start-button-mobile"
        :disabled="loading"
        @click="handleClick"
      >
        <icon
          v-if="rateLimitLocked"
          class="button-icon"
          name="lock"
          variant="white"
        />
        {{ buttonText }}
      </button>
    </div>
  </div>
</template>

<script>
// There is some overlap between this and the new mixin to setup the learning path
// This could be refactored to use the new mixin
import Icon from '@components/singletons/icon';
import RateLimitMixin from '@/mixins/rate-limit';
import { stripHtml } from '@/utils';
import ModalBase from '@components/modals/ModalBase';


export default {
  name: 'StartWidget',
  components: { Icon, ModalBase },
  mixins: [RateLimitMixin],
  props: {
    config: {
      type: Object,
      required: true,
    },
    dashboard: {
      type: Object,
      required: true,
    },
    identifier: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      startedLoading: false,
      loadingFlashcard: false,
      loadingFlashcardSession: false,
      loadingAttachment: false,
      loadingFlashcardStates: false,
      showModal: false,
    };
  },
  computed: {
    attachment() {
      if (!this.attachmentId) return null;
      return this.$store.getters['attachments/get'][this.attachmentId] || {};
    },
    attachmentId() {
      return this.config?.config?.attachmentId;
    },
    attachmentUrl() {
      return this.attachment?.url;
    },
    buttonText() {
      if (this.loading) {
        return 'Loading...';
      }

      return this.config?.config?.startButtonText || 'See Answer Choices';
    },
    categoryId() {
      return this.firstFlashcard.categoryId;
    },
    description() {
      return this.config?.config?.description || '';
    },
    fallbackCategoryId() {
      return this.config?.config?.startCategoryId;
    },
    firstFlashcard() {
      return this.unansweredFlashcards[0] || {};
    },
    flashcard() {
      return this.$store.getters['flashcards/flashcards'][this.firstFlashcard?.flashcardId];
    },
    flashcardId() {
      return this.firstFlashcard.flashcardId;
    },
    flashcardSession() {
      return this.$store.getters['flashcards/currentSession'];
    },
    infoHtml() {
      return this.config?.config?.info || '';
    },
    learningPathFlashcards() {
      return this.$store.getters['learningPath/flashcards'];
    },
    loading() {
      return !this.startedLoading || this.loadingFlashcard || this.loadingAttachment || this.loadingFlashcardStates || this.loadingFlashcardSession;
    },
    questionStem() {
      if (!this.flashcard?.question) return '';
      return this.loading ? '' : stripHtml(this.flashcard?.question) || '';
    },
    rateLimitLocked() {
      if (this.$store.getters['session/hasPaid']) {
        return false;
      }
      return this.rateLimitingEnabled && this.questionsRemaining <= 0;
    },
    showStartWidget() {
      return this.unansweredFlashcards.length > 0;
    },
    startButtonText() {
      return this.loading ? 'Loading...' : this.config?.config?.startButtonText;
    },
    title() {
      return this.config?.config?.title;
    },
    unansweredFallbackCategoryFlashcards() {
      return this.$store.getters['flashcards/currentSession']
        .filter((flashcardId) => {
          const key = `${flashcardId}-category-${this.fallbackCategoryId}`;
          return !this.$store.getters['flashcardStates/flashcardStates'][key];
        })
        .map((flashcardId) => {
          return {
            flashcardId,
            categoryId: this.fallbackCategoryId,
          };
        });
    },
    unansweredStartFlashcards() {
      if (this.config?.config?.startFlashcards) {
        return this.config?.config?.startFlashcards.filter((flashcard) => {
          const key = `${flashcard.flashcardId}-category-${flashcard.categoryId}`;
          return !this.$store.getters['flashcardStates/flashcardStates'][key];
        });
      } else {
        return false;
      }
    },
    unansweredFlashcards() {
      if (this.unansweredStartFlashcards.length) {
        return this.unansweredStartFlashcards;
      }
      if (this.learningPathFlashcards.length) {
        return this.learningPathFlashcards;
      }
      return this.unansweredFallbackCategoryFlashcards;
    },
    useLearningPath() {
      return !this.unansweredStartFlashcards?.length > 0 && this.learningPathFlashcards.length > 0;
    },
    useFallbackCategory() {
      return !this.unansweredStartFlashcards?.length > 0 && !this.learningPathFlashcards.length > 0 && this.unansweredFallbackCategoryFlashcards.length > 0;
    },
  },
  watch: {
    flashcardId() {
      this.loadFlashcard();
    },
  },
  created() {
    this.$analytics.events.viewedStartWidget();
    this.loadFlashcardStates();
  },
  mounted() {
    this.loadFlashcardSession();
    this.startedLoading = true;
    this.loadAttachment();
    this.loadLearningPathFromApi();
    if (this.flashcardId !== this.flashcard?.id) {
      this.loadFlashcard();
    }
  },
  methods: {
    handleClick() {
      if (this.rateLimitLocked) {
        return this.showUpSellModal();
      }
      return this.gotoFlashcard();
    },
    showUpSellModal() {
      const source = { type: 'Start Widget' };
      const analyticParams = {
        dashboard: this.dashboard,
        destination: 'Upgrade Screen',
        widget: this.config,
      };
      this.$analytics.events.selectedRateLimitWidget(analyticParams);
      this.$store.dispatch('ui/toggleUpsellModal', { show: true, source });
    },
    gotoFlashcard() {
      this.$analytics.events.selectedStartWidget();
      if (this.useFallbackCategory) {
        return this.$router.push({
          name: 'categories#flashcard-v2',
          params: {
            categoryId: this.fallbackCategoryId,
            flashcardId: this.unansweredFlashcards[0].flashcardId,
          },
        });
      }
      this.setLearningPath();
      return this.$router.push({
        name: 'learningPath#show',
        params: {
          categoryId: this.categoryId,
          flashcardId: this.flashcardId,
        },
      });
    },
    loadFlashcardSession() {
      if (!this.$access(this.$store, 'category', this.fallbackCategoryId)) {
        return;
      }
      this.loadingFlashcardSession = true;
      this.$store.dispatch('flashcards/createFlashcardSession', { categoryId: this.fallbackCategoryId, rateLimited: this.rateLimitingEnabled })
        .finally(() => {
          this.loadingFlashcardSession = false;
        });
    },
    loadFlashcard() {
      this.loadingFlashcard = true;
      this.$store.dispatch('flashcards/fetchFlashcards', { ids: [this.flashcardId], rateLimited: this.rateLimitingEnabled })
        .finally(() => {
        this.loadingFlashcard = false;
      });
    },
    loadAttachment() {
      this.loadingAttachment = true;
      this.$store.dispatch('attachments/fetch', { ids: [this.attachmentId] })
        .finally(() => {
          this.loadingAttachment = false;
        });
    },
    loadFlashcardStates() {
      this.loadingFlashcardStates = true;
      this.$store.dispatch('flashcardStates/fetchStates', ['flashcards'])
        .finally(() => {
          this.loadingFlashcardStates = false;
        });
    },
    setLearningPath() {
      if (this.useLearningPath) {
        return; // learning path is already set
      }
      this.$store.commit('learningPath/setFlashcards', [...this.unansweredStartFlashcards]);
      this.$store.commit('learningPath/setGroupingId', this.config?.config?.groupingId);
      this.$store.commit('learningPath/setFallbackCategoryId', this.fallbackCategoryId);
    },
    loadLearningPathFromApi() {
      this.$store.dispatch('learningPath/fetchLearningPath');
    },
  },
};
</script>

<style scoped lang="scss">
.start-widget-container.skeleton {
  background-color: #f5f5f5;
  color: #f5f5f5;

  .column-one {
    .app-icon {
      background-color: #999;
      border-color: #999;
      opacity: 0.3;
    }
  }

}

.start-widget-container {
  display: grid;
  grid-template-columns: 1fr;
  padding: 20px;
  border-radius: 10px;
  background-color: var(--bg-widget-primary);
  color: white;
  row-gap: 20px;
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);

  @include breakpoint(desktop) {
    grid-template-columns: 1fr 1fr;
  }

  .column-one {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: rem-calc(4);

    .info-button {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      align-self: flex-end;

      .clear-button {
        background-color: transparent;
        padding: 0;
        margin: 0;
      }

      @include breakpoint(desktop) {
        align-self: flex-start;
      }
    }

    .spacer {
      display: none;

      @include breakpoint(desktop) {
        display: block;
        height: 20px;
        flex-grow: 1;
      }
    }

    .app-icon {
      width: 50px;
      height: 50px;
      background-color: #f5f5f5;
      border-radius: 50%;
      border: 1px solid #ccc;
      align-self: center;

      @include breakpoint(desktop) {
        align-self: flex-start;
      }
    }

    .title {
      font-size: rem-calc(24);
      font-weight: 600;
      text-align: center;
      line-height: rem-calc(30);

      @include breakpoint(desktop) {
        font-size: rem-calc(36);
        line-height: rem-calc(44);
        text-align: left;
      }
    }

    .description {
      text-align: center;
      font-size: rem-calc(14);
      font-weight: 600;
      line-height: rem-calc(20);

      @include breakpoint(desktop) {
        font-size: rem-calc(20);
        line-height: rem-calc(30);
        text-align: left;
      }
    }
  }
  .column-two {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: stretch;
    .question-box {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      background-color: white;
      border-radius: rem-calc(8);
      padding: rem-calc(20);
      gap: rem-calc(20);

      .question-stem {
        font-size: rem-calc(18);
        font-weight: 400;
        line-height: rem-calc(28);
        color: #232323;
        display: -webkit-box;
        -webkit-line-clamp: 5;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: rem-calc(144);

        &.blurred {
          filter: blur(4px);
        }
      }
      .start-button {
        width: 100%;
        padding: 10px;
        border: none;
        border-radius: 5px;
        background-color: black;
        color: white;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;

        &.no-credits-left {
          background-color: #E98626;
        }

        .button-icon {
          margin-right: 4px;
          width: 12px;
        }

        &:hover {
          opacity: 0.7;
        }

        &.loading {
          opacity: 0.2;
          cursor: not-allowed;
        }
      }
    }
  }
}
.start-widget-container {
  position: relative;
  padding: 16px;
}

.start-widget-container .column-two { align-items: center; }
.start-widget-container .column-two .question-box {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  gap: 12px;
  box-sizing: border-box;
  margin-left: 0;
  margin-right: 0;
}
.start-widget-container .column-two .start-button--mobile {
  width: 100%;
  height: 48px;
  margin-top: 14px;
  border-radius: 8px;
  box-sizing: border-box;
  margin-left: 0;
  margin-right: 0;
  background: #000;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  border: none;
  cursor: pointer;
}
.start-widget-container .column-two .start-button--mobile.no-credits-left { background: #E98626; }
.start-widget-container .column-two .start-button--mobile .button-icon { margin-right: 4px; width: 12px; }
.start-widget-container .column-two .start-button--mobile:hover { opacity: 0.7; }
.start-widget-container .column-two .start-button--mobile.loading { opacity: 0.2; cursor: not-allowed; }
.start-widget-container .column-two .question-box .start-button { display: none; }
.start-widget-container .column-two .question-box .question-stem {
  font-size: 16px;
  line-height: 22px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  min-height: 67px;
}

@include breakpoint(desktop) {
  .start-widget-container {
    padding: 20px;
  }

  .start-widget-container .column-two { align-items: stretch; }
  .start-widget-container .column-two .question-box {
    width: auto !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    box-sizing: content-box !important;
    border-radius: rem-calc(8);
    padding: rem-calc(20);
    gap: rem-calc(20);
  }

  .start-widget-container .column-two .question-box .question-stem {
    font-size: rem-calc(18);
    line-height: rem-calc(28);
    -webkit-line-clamp: 5;
    min-height: rem-calc(144);
  }

  .start-widget-container .column-two .question-box .start-button { display: block; }
  .start-widget-container .column-two .start-button--mobile { display: none; }
}
</style>
